import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Send, Globe, Copy, Check, Eye, Loader2, FileText, MapPin, Clock,
  Users, BookOpen, Paperclip, ChevronDown, ChevronRight, ExternalLink,
  ArrowUp, ArrowDown, X, Sparkles, Search, Plus, Image as ImageIcon,
  Pencil, Hash, Phone, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { fillPlaceholders, buildContext } from "@/lib/placeholders";
import { api, apiErrorMessage } from "@/lib/api";
import ImagePicker from "@/components/media/ImagePicker";

export default function TravelDocTab({ tripId, trip }) {
  const qc = useQueryClient();
  const [copied, setCopied] = useState(false);
  const [expandedDays, setExpandedDays] = useState(() => new Set([1]));
  // Reserva actualmente en edición (modal §2 · Día por día). null = cerrado.
  const [editingReserva, setEditingReserva] = useState(null);
  // Estado del picker unificado (prólogo o día concreto — 2026-C.9)
  // scope: 'prologue' | 'day'; para 'day' llevamos {date, after_reserva_id}.
  const [picker, setPicker] = useState(null);
  // Marca al publicar: 'et' (Espíritu Travel) o 'vdv' (Viaja de Verdad).
  // Es independiente del dominio de SOFI 2.0 — controla en qué dominio se
  // sirve el TD al cliente. Se inicializa desde `data.brand` al cargar.
  const [publishBrand, setPublishBrand] = useState("et");

  const { data, isLoading, error } = useQuery({
    queryKey: ["td", tripId],
    queryFn: async () => (await api.get(`/trips/${tripId}/td`)).data,
    enabled: !!tripId,
  });

  React.useEffect(() => {
    if (data?.brand && (data.brand === "et" || data.brand === "vdv")) {
      setPublishBrand(data.brand);
    }
  }, [data?.brand]);

  const draft = useMutation({
    mutationFn: (payload) => api.put(`/trips/${tripId}/td`, payload).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["td", tripId] }),
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const publish = useMutation({
    mutationFn: (brand) => api.post(`/trips/${tripId}/td/publish`, { brand }).then((r) => r.data),
    onSuccess: (r) => {
      const bLabel = r.brand === "vdv" ? "Viaja de Verdad" : "Espíritu Travel";
      if (r.brand === "vdv" && r.external && !r.external.ok) {
        toast.warning(`Publicado local (${bLabel}) — la herramienta VdV está caída, reintenta más tarde.`, { duration: 8000 });
      } else {
        toast.success(`Travel Document publicado (${bLabel})`);
      }
      qc.invalidateQueries({ queryKey: ["td", tripId] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const retryExternal = useMutation({
    mutationFn: () => api.post(`/trips/${tripId}/td/retry-external`).then((r) => r.data),
    onSuccess: (r) => {
      if (r.ok) toast.success("Publicado correctamente en Viaja de Verdad");
      else toast.error(`VdV sigue caído: ${r.external?.error || "error"}`);
      qc.invalidateQueries({ queryKey: ["td", tripId] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const unpublish = useMutation({
    mutationFn: () => api.post(`/trips/${tripId}/td/unpublish`).then((r) => r.data),
    onSuccess: () => { toast.success("Travel Document despublicado"); qc.invalidateQueries({ queryKey: ["td", tripId] }); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  if (isLoading) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Cargando Travel Document…</div>;
  if (error) return <div className="text-sm text-destructive">{apiErrorMessage(error)}</div>;
  if (!data) return null;

  const { status, public_token, days = [], available = {}, selection = {}, general_notes, cover_image, destination, brand: currentBrand, public_url: backendPublicUrl, external_publish_pending, external_public_url } = data;
  const isPublished = status === "published";
  // publicUrl viene del backend con el dominio correcto según la marca (et → sofi.espiritutravel.com,
  // vdv → sofi.viajadverdad.com). Fallback al origin actual sólo en dev.
  const publicUrl = external_public_url || backendPublicUrl || (public_token ? `${window.location.origin}/trip/${public_token}` : null);

  const prologueGuides = selection.prologue_guides || [];
  const dayInserts = selection.day_inserts || {};
  const selectedVouchers = new Set(selection.voucher_ids || []);

  // Catálogo de guías disponibles (aplanado con etiqueta de origen)
  const allGuides = (() => {
    const flat = [];
    for (const g of (available.guides?.generales || [])) flat.push({ ...g, _origin: "Generales", _kind: "general" });
    const paisName = available.guides?.pais?.nombre;
    for (const g of (available.guides?.pais?.guides || [])) flat.push({ ...g, _origin: paisName || "País", _kind: "country" });
    for (const cityEntry of (available.guides?.ciudades || [])) {
      for (const g of (cityEntry.guides || [])) flat.push({ ...g, _origin: cityEntry.city, _kind: "city" });
    }
    return flat;
  })();

  // Vouchers agrupados por reserva_id → dict { reserva_id: [voucher,...] }
  const vouchersByReserva = (() => {
    const m = {};
    for (const v of (available.vouchers || [])) {
      const rid = v.reserva_id;
      if (!rid) continue;
      (m[rid] = m[rid] || []).push(v);
    }
    return m;
  })();

  // Contador total de guías insertadas (prólogo + días)
  const totalGuidesSelected = prologueGuides.length
    + Object.values(dayInserts).reduce((n, arr) => n + (arr?.length || 0), 0);
  const totalVouchersSelected = selectedVouchers.size;
  const totalPlantillasAsignadas = days.reduce(
    (n, d) => n + d.items.filter((i) => i.item_type !== "guide" && i.plantilla_id).length, 0,
  );
  const totalItems = days.reduce(
    (n, d) => n + d.items.filter((i) => i.item_type !== "guide").length, 0,
  );

  const toggleVoucher = (vid) => {
    const list = [...(selection.voucher_ids || [])];
    const i = list.indexOf(vid);
    if (i >= 0) list.splice(i, 1); else list.push(vid);
    draft.mutate({ voucher_ids: list });
  };
  const toggleDay = (dn) => {
    const s = new Set(expandedDays);
    s.has(dn) ? s.delete(dn) : s.add(dn);
    setExpandedDays(s);
  };

  const removeFromDay = (date, guideItemIdx) => {
    // guideItemIdx = índice dentro de la lista completa de inserts del día
    const current = [...(dayInserts[date] || [])];
    current.splice(guideItemIdx, 1);
    const next = { ...dayInserts, [date]: current };
    draft.mutate({ day_inserts: next });
  };

  const addToPrologue = (gid) => {
    draft.mutate({ prologue_guides: [...prologueGuides, gid] });
  };
  const removeFromPrologue = (idx) => {
    const list = [...prologueGuides];
    list.splice(idx, 1);
    draft.mutate({ prologue_guides: list });
  };
  const moveInPrologue = (idx, dir) => {
    const list = [...prologueGuides];
    const t = idx + dir;
    if (t < 0 || t >= list.length) return;
    [list[idx], list[t]] = [list[t], list[idx]];
    draft.mutate({ prologue_guides: list });
  };

  const addToDay = (date, after_reserva_id, gid) => {
    const current = dayInserts[date] || [];
    const next = { ...dayInserts, [date]: [...current, { plantilla_id: gid, after_reserva_id: after_reserva_id || null }] };
    draft.mutate({ day_inserts: next });
  };

  const copyUrl = async () => {
    if (!publicUrl) return;
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-6" data-testid="travel-doc-tab">
      {/* Header · estado + publicar */}
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Travel Document</h2>
              <Badge variant={isPublished ? "default" : "secondary"} data-testid="td-status">
                {isPublished ? "Publicado" : "Borrador"}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Se compone automáticamente a partir de las plantillas TD asignadas en cada reserva + las guías y vouchers que selecciones abajo.
            </p>
            {publicUrl && isPublished && (
              <div className="mt-3 flex items-center gap-2">
                <div className="flex min-w-0 items-center gap-1.5 rounded-md border bg-muted/50 px-3 py-1.5 text-xs">
                  <Globe className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate font-mono" data-testid="td-public-url">{publicUrl}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={copyUrl} data-testid="td-copy-url">
                  {copied ? <><Check className="mr-1 h-3.5 w-3.5" />Copiado</> : <><Copy className="mr-1 h-3.5 w-3.5" />Copiar</>}
                </Button>
                <Button variant="ghost" size="sm" asChild data-testid="td-open-url">
                  <a href={publicUrl} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-1 h-3.5 w-3.5" />Abrir</a>
                </Button>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {/* Selector de marca — controla en qué dominio se comparte el TD.
                Es independiente del dominio interno de SOFI 2.0. Los tokens
                son universales: al cambiar de marca sólo cambia el host del
                enlace, la URL antigua sigue funcionando si el DNS de la otra
                marca sigue activo. */}
            <div className="flex items-center gap-2 text-xs">
              <label className="font-medium text-muted-foreground">Marca:</label>
              <div className="inline-flex rounded-md border p-0.5" data-testid="td-brand-picker">
                <button
                  type="button"
                  onClick={() => setPublishBrand("et")}
                  className={`px-2.5 py-1 rounded ${publishBrand === "et" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                  data-testid="td-brand-et"
                >
                  Espíritu Travel
                </button>
                <button
                  type="button"
                  onClick={() => setPublishBrand("vdv")}
                  className={`px-2.5 py-1 rounded ${publishBrand === "vdv" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
                  data-testid="td-brand-vdv"
                >
                  Viaja de Verdad
                </button>
              </div>
            </div>
            {isPublished && currentBrand && currentBrand !== publishBrand && (
              <div className="text-[10px] text-amber-700" data-testid="td-brand-warning">
                Marca actual: <b>{currentBrand === "vdv" ? "Viaja de Verdad" : "Espíritu Travel"}</b>. Al republicar cambiará a <b>{publishBrand === "vdv" ? "Viaja de Verdad" : "Espíritu Travel"}</b> y el enlace pasará al nuevo dominio.
              </div>
            )}
            {isPublished && external_publish_pending && (
              <div className="flex items-center gap-2 rounded bg-amber-100 border border-amber-300 px-2 py-1.5 text-[11px] text-amber-900" data-testid="td-external-pending">
                <AlertTriangle className="h-3.5 w-3.5" />
                <span>VdV no confirmó la publicación. Reintenta cuando esté disponible.</span>
                <Button size="sm" variant="outline" className="ml-1 h-6 text-[10px]" onClick={() => retryExternal.mutate()} disabled={retryExternal.isPending} data-testid="td-retry-external">
                  {retryExternal.isPending ? "Reintentando…" : "Reintentar"}
                </Button>
              </div>
            )}
            {isPublished ? (
              <>
                <Button variant="outline" onClick={() => publish.mutate(publishBrand)} disabled={publish.isPending} data-testid="td-republish">
                  <Send className="mr-2 h-4 w-4" />
                  {publish.isPending ? "Actualizando…" : "Actualizar publicación"}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive" onClick={() => unpublish.mutate()} disabled={unpublish.isPending} data-testid="td-unpublish">
                  Despublicar
                </Button>
              </>
            ) : (
              <Button onClick={() => publish.mutate(publishBrand)} disabled={publish.isPending} size="lg" data-testid="td-publish">
                <Send className="mr-2 h-4 w-4" />
                {publish.isPending ? "Publicando…" : "Publicar Travel Document"}
              </Button>
            )}
          </div>
        </div>

        {/* Foto de portada — mismo picker que usa el creador de itinerarios */}
        <div className="mt-5 border-t pt-4">
          <TdCoverPicker
            value={cover_image || ""}
            destination={destination || ""}
            onChange={(url) => draft.mutate({ cover_image: url || "" })}
          />
        </div>

        {/* Métricas rápidas */}
        <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <MetricPill label="Días" value={days.length} />
          <MetricPill label="Plantillas asignadas" value={`${totalPlantillasAsignadas}/${totalItems}`} testId="td-metric-plantillas" />
          <MetricPill label="Guías incluidas" value={totalGuidesSelected} testId="td-metric-guides" />
          <MetricPill label="Vouchers incluidos" value={totalVouchersSelected} testId="td-metric-vouchers" />
        </div>
      </Card>

      {/* §1 · Prólogo — guías antes del día 1 (2026-C.9) */}
      <section data-testid="td-prologue">
        <SectionTitle
          icon={<BookOpen className="h-4 w-4" />}
          title="§1 · Introducción (antes del día 1)"
          caption="Guías de bienvenida / país que aparecen al principio del Travel Document."
        />
        <PrologueList
          guideIds={prologueGuides}
          guidesMeta={selection.prologue_guides_meta || []}
          allGuides={allGuides}
          onRemove={removeFromPrologue}
          onMove={moveInPrologue}
          onOpenPicker={() => setPicker({ scope: "prologue" })}
        />
      </section>

      {/* §2 · Vista compuesta día por día con inserciones inline */}
      <section data-testid="td-composed-view">
        <SectionTitle icon={<FileText className="h-4 w-4" />} title="§2 · Día por día" caption="Edita las plantillas desde la pestaña Template de cada reserva. Pulsa «+ Añadir información» entre actividades para insertar guías dentro de un día." />
        <div className="space-y-2">
          {days.map((day) => (
            <DayCard
              key={day.day_num}
              day={day}
              trip={trip}
              open={expandedDays.has(day.day_num)}
              onToggle={() => toggleDay(day.day_num)}
              vouchersByReserva={vouchersByReserva}
              selectedVouchers={selectedVouchers}
              onToggleVoucher={toggleVoucher}
              onOpenPicker={(after_reserva_id) => setPicker({ scope: "day", date: day.date, after_reserva_id })}
              onRemoveGuide={(guideItemIdx) => day.date && removeFromDay(day.date, guideItemIdx)}
              onEditReserva={setEditingReserva}
            />
          ))}
        </div>
      </section>

      {/* §3 · Notas generales del TD (opcional) */}
      <section data-testid="td-notes">
        <SectionTitle icon={<FileText className="h-4 w-4" />} title="§3 · Notas generales (opcional)" caption="Texto libre que aparecerá al inicio del Travel Document (contacto agencia, emergencias, tips…)." />
        <RichTextEditor
          value={general_notes || ""}
          onChange={(html) => draft.mutate({ general_notes: html })}
          placeholder="Ej: Nuestro equipo está disponible 24/7 en el +34 XXX XXX XXX…"
          minRows={6}
          testId="td-general-notes"
        />
      </section>

      {/* Picker global de guías (Dialog) */}
      <GuidePickerDialog
        open={!!picker}
        onClose={() => setPicker(null)}
        context={picker}
        allGuides={allGuides}
        onPick={(gid) => {
          if (!picker) return;
          if (picker.scope === "prologue") addToPrologue(gid);
          else if (picker.scope === "day") addToDay(picker.date, picker.after_reserva_id, gid);
          setPicker(null);
        }}
      />

      {/* Modal ligero para editar la reserva desde el TD sin abandonar la vista */}
      <EditReservaTemplateModal
        tripId={tripId}
        reservaItem={editingReserva}
        onClose={() => setEditingReserva(null)}
      />
    </div>
  );
}


// ---------- Sub-componentes ----------

function MetricPill({ label, value, testId }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold">{value}</div>
    </div>
  );
}

function SectionTitle({ icon, title, caption }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2 font-semibold">{icon}{title}</div>
      {caption && <p className="text-xs text-muted-foreground mt-0.5">{caption}</p>}
    </div>
  );
}

function EmptyBox({ text }) {
  return <div className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">{text}</div>;
}

function DayCard({ day, trip, open, onToggle, vouchersByReserva, selectedVouchers, onToggleVoucher, onOpenPicker, onRemoveGuide, onEditReserva }) {
  const dateStr = day.date ? new Date(day.date + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "Sin fecha";
  const reservaItems = day.items.filter((i) => i.item_type !== "guide");
  const guideItems = day.items.filter((i) => i.item_type === "guide");
  const nAsignadas = reservaItems.filter((i) => i.plantilla_id).length;

  // Índice de guías (posición dentro de la lista de inserts del día) para
  // saber cuál quitar. Va en el orden de aparición en day.items.
  let _gIdx = -1;
  const withGuideIdx = day.items.map((it) => {
    if (it.item_type === "guide") { _gIdx += 1; return { ...it, _idx: _gIdx }; }
    return it;
  });

  return (
    <div className="rounded-lg border bg-card" data-testid={`td-day-${day.day_num}`}>
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-muted/40"
        onClick={onToggle}
        data-testid={`td-day-toggle-${day.day_num}`}
      >
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">Día {day.day_num}</span>
          <span className="text-sm font-medium">{day.city || "—"}</span>
          <span className="text-xs text-muted-foreground">· {dateStr}</span>
        </div>
        <span className="ml-auto text-xs text-muted-foreground">
          {nAsignadas}/{reservaItems.length} con plantilla
          {guideItems.length > 0 ? ` · ${guideItems.length} guía${guideItems.length === 1 ? "" : "s"}` : ""}
        </span>
      </button>
      {open && (
        <div className="border-t px-4 py-3 space-y-2">
          {/* Botón inicial: guía al principio del día (before any reserva) */}
          {day.date && (
            <InsertButton onClick={() => onOpenPicker(null)} testid={`td-day-${day.day_num}-insert-top`} />
          )}
          {withGuideIdx.map((it, i) => (
            <React.Fragment key={it.item_type === "guide" ? `g-${it.guide_id}-${it._idx}` : `r-${it.reserva_id}`}>
              {it.item_type === "guide" ? (
                <GuidePreviewRow
                  guide={it}
                  onRemove={() => onRemoveGuide(it._idx)}
                  testid={`td-day-${day.day_num}-guide-${it._idx}`}
                />
              ) : (
                <ReservaRow
                  item={it}
                  trip={trip}
                  vouchers={vouchersByReserva?.[it.reserva_id] || []}
                  selectedVouchers={selectedVouchers}
                  onToggleVoucher={onToggleVoucher}
                  onEdit={() => onEditReserva(it)}
                />
              )}
              {/* Botón "+ Añadir info" DESPUÉS de cada reserva (para poder
                  insertar entre actividades). Después de una guía no lo
                  ponemos: el operador puede añadir otra usando el botón que
                  ya está debajo de la siguiente reserva. */}
              {it.item_type !== "guide" && day.date && (
                <InsertButton
                  onClick={() => onOpenPicker(it.reserva_id)}
                  testid={`td-day-${day.day_num}-insert-after-${it.reserva_id}`}
                />
              )}
            </React.Fragment>
          ))}
          {reservaItems.length === 0 && !day.date && (
            <p className="text-xs italic text-muted-foreground">Este día no tiene reservas ni fecha; añade guías desde el día correcto.</p>
          )}
        </div>
      )}
    </div>
  );
}

function InsertButton({ onClick, testid }) {
  return (
    <div className="flex items-center justify-center py-0.5">
      <button
        type="button"
        onClick={onClick}
        data-testid={testid}
        className="group flex items-center gap-1 rounded-full border border-dashed border-muted-foreground/40 bg-background px-2.5 py-0.5 text-[10px] font-medium text-muted-foreground transition hover:border-primary hover:bg-primary/5 hover:text-primary"
      >
        <Plus className="h-3 w-3" /> Añadir información aquí
      </button>
    </div>
  );
}

function GuidePreviewRow({ guide, onRemove, testid }) {
  return (
    <div className="rounded-md border border-l-4 border-primary/60 bg-primary/5 pl-3 pr-2 py-2" data-testid={testid}>
      <div className="flex items-center gap-2">
        <BookOpen className="h-3.5 w-3.5 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{guide.name}</span>
        {guide.category && <Badge variant="outline" className="text-[9px]">{guide.category}</Badge>}
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onRemove} data-testid={`${testid}-remove`} title="Quitar esta guía">
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function PrologueList({ guideIds, guidesMeta, allGuides, onRemove, onMove, onOpenPicker }) {
  const byId = new Map(allGuides.map((g) => [g.id, g]));
  const metaById = new Map((guidesMeta || []).map((m) => [m.id, m]));
  return (
    <div className="space-y-2" data-testid="td-prologue-list">
      {guideIds.length === 0 ? (
        <EmptyBox text="Aún no has añadido ninguna guía al prólogo." />
      ) : (
        guideIds.map((gid, idx) => {
          const g = byId.get(gid) || metaById.get(gid);
          const origin = g?._origin || g?.location || "";
          return (
            <div key={`${gid}-${idx}`} className="rounded-md border border-l-4 border-primary/60 bg-primary/5 px-3 py-2 flex items-center gap-2" data-testid={`td-prologue-item-${idx}`}>
              <BookOpen className="h-3.5 w-3.5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium">{g?.name || <span className="italic text-muted-foreground">Guía eliminada</span>}</div>
                {origin && <div className="text-[10px] text-muted-foreground">{origin}</div>}
              </div>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(idx, -1)} disabled={idx === 0}><ArrowUp className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onMove(idx, 1)} disabled={idx === guideIds.length - 1}><ArrowDown className="h-3.5 w-3.5" /></Button>
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onRemove(idx)}><X className="h-3.5 w-3.5" /></Button>
            </div>
          );
        })
      )}
      <Button variant="outline" size="sm" onClick={onOpenPicker} data-testid="td-prologue-add">
        <Plus className="mr-1 h-3.5 w-3.5" />Añadir guía al prólogo
      </Button>
    </div>
  );
}

function ReservaRow({ item, trip, vouchers, selectedVouchers, onToggleVoucher, onEdit }) {
  const ctx = useMemo(() => buildContext({
    reserva: {
      hour_entry: item.hour_entry,
      date_entry: item.date_entry,
      num_ad: item.num_ad,
      num_ch: item.num_ch,
      city: item.city,
      country: item.country,
      service: item.service,
      provider_name: item.provider_name,
      duration: item.duration,
      start_location: item.start_location,
      end_location: item.end_location,
    },
    trip,
  }), [item, trip]);

  const html = fillPlaceholders(item.sales_description || "", ctx);
  const hasContent = !!(item.sales_description || "").trim();

  return (
    <div className="border-l-2 border-primary/30 pl-3" data-testid={`td-item-${item.reserva_id}`}>
      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-sm">
        {item.hour_entry && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Clock className="h-3 w-3" />{item.hour_entry}
          </span>
        )}
        <span className="font-medium">{item.service || "—"}</span>
        {/* Icono lápiz para editar campos operativos + descripción sin abandonar
            la vista TD (2026-08 · petición equipo). El modal auto-cierra al
            guardar e invalida las queries del TD para refrescar. */}
        <button
          type="button"
          onClick={() => onEdit && onEdit()}
          className="ml-0.5 inline-flex h-7 items-center gap-1 rounded-md border border-primary/40 bg-primary/5 px-2 text-[11px] font-medium text-primary hover:border-primary hover:bg-primary/10"
          title="Editar template desde aquí"
          data-testid={`td-item-edit-${item.reserva_id}`}
        >
          <Pencil className="h-3.5 w-3.5" />Editar
        </button>
        {item.plantilla_name ? (
          <Badge variant="outline" className="gap-1 text-[10px]"><Check className="h-2.5 w-2.5" />{item.plantilla_name}</Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">Sin plantilla</Badge>
        )}
        {(item.num_ad + item.num_ch) > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Users className="h-3 w-3" />{item.num_ad + item.num_ch}
          </span>
        )}
      </div>
      {hasContent ? (
        <div
          className="prose prose-sm max-w-none text-sm text-foreground [&_a]:text-primary [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      ) : (
        <p className="text-xs italic text-muted-foreground">
          Sin descripción. Ve a la pestaña Template de esta reserva para añadir una plantilla o escribir texto libre.
        </p>
      )}
      {/* Vouchers inline por reserva — el operador activa el que quiera adjuntar al TD */}
      {vouchers.length > 0 && (
        <div className="mt-2 space-y-1 rounded-md border bg-muted/30 p-2" data-testid={`td-item-vouchers-${item.reserva_id}`}>
          <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            Vouchers de esta reserva
          </div>
          {vouchers.map((v) => (
            <label
              key={v.voucher_id}
              className="flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 hover:bg-background"
              data-testid={`td-item-voucher-${v.voucher_id}`}
            >
              <Checkbox
                checked={selectedVouchers.has(v.voucher_id)}
                onCheckedChange={() => onToggleVoucher(v.voucher_id)}
                data-testid={`td-item-voucher-check-${v.voucher_id}`}
              />
              <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="flex-1 truncate text-xs">{v.filename}</span>
              <span className="text-[10px] text-muted-foreground">{formatSize(v.size)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Buscador manual de guías ────────────────────────────────────────────────
function GuidePickerDialog({ open, onClose, context, allGuides, onPick }) {
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");

  // Reset filtro al abrir
  React.useEffect(() => { if (open) { setQ(""); setScope("all"); } }, [open]);

  if (!open) return null;

  const term = q.trim().toLowerCase();
  const filtered = allGuides.filter((g) => {
    if (scope !== "all" && g._kind !== scope) return false;
    if (!term) return true;
    return (g.name || "").toLowerCase().includes(term)
      || (g._origin || "").toLowerCase().includes(term)
      || (g.category || "").toLowerCase().includes(term)
      || (g.excerpt || "").toLowerCase().includes(term);
  });

  const title = context?.scope === "prologue"
    ? "Añadir guía al prólogo"
    : context?.after_reserva_id
      ? "Insertar guía después de la actividad seleccionada"
      : "Insertar guía al inicio del día";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="td-guide-picker"
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-lg bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <div className="text-sm font-semibold">{title}</div>
            <div className="text-[11px] text-muted-foreground">La misma guía se puede añadir varias veces en distintos días.</div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} data-testid="td-guide-picker-close"><X className="h-4 w-4" /></Button>
        </div>
        <div className="space-y-2 border-b p-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, ciudad, categoría…" className="pl-8" data-testid="td-guide-picker-search" autoFocus />
            </div>
            <div className="flex gap-1 rounded-md border p-0.5">
              {[["all", "Todas"], ["general", "Generales"], ["country", "País"], ["city", "Ciudad"]].map(([k, lbl]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setScope(k)}
                  className={`rounded px-2.5 py-1 text-xs ${scope === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                  data-testid={`td-guide-picker-scope-${k}`}
                >{lbl}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {term ? `Sin coincidencias para "${q}".` : "No hay guías disponibles con este filtro."}
            </div>
          ) : (
            <div className="divide-y">
              {filtered.slice(0, 100).map((g) => (
                <button
                  type="button"
                  key={g.id}
                  onClick={() => onPick(g.id)}
                  className="flex w-full items-start gap-3 px-3 py-2 text-left hover:bg-muted/50"
                  data-testid={`td-guide-picker-item-${g.id}`}
                >
                  <GuideOriginIcon kind={g._kind} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{g.name}</div>
                    <div className="text-[11px] text-muted-foreground">{g._origin} · {catLabel(g.category || "other")}</div>
                  </div>
                  <Plus className="h-4 w-4 shrink-0 text-primary" />
                </button>
              ))}
              {filtered.length > 100 && (
                <div className="p-2 text-center text-[11px] text-muted-foreground">
                  {filtered.length - 100} resultados más… refina la búsqueda.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GuideOriginIcon({ kind }) {
  const Icon = kind === "city" ? MapPin : kind === "country" ? Globe : Sparkles;
  return <Icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" />;
}

const CAT_LABELS = {
  city_guide: "Guía general",
  restaurants: "Restaurantes",
  bars_nightlife: "Bares & Nocturno",
  museums: "Museos",
  activities: "Actividades",
  transportation: "Transporte",
  shopping: "Compras",
  general_info: "Información general",
  other: "Otros",
};
function catLabel(cat) { return CAT_LABELS[cat] || cat; }

function formatSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}


// ─── Cover picker (mismo patrón que CotizadorDetail.CoverImagePicker) ────────
function TdCoverPicker({ value, destination, onChange }) {
  const [open, setOpen] = useState(false);
  const [imgError, setImgError] = useState(false);
  React.useEffect(() => { setImgError(false); }, [value]);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const src = value ? (String(value).startsWith("/api/") ? `${BACKEND}${value}` : value) : "";
  return (
    <div className="flex items-start gap-3" data-testid="td-cover-picker">
      {src ? (
        <div className="relative h-28 w-48 shrink-0 overflow-hidden rounded-md border bg-muted">
          {imgError ? (
            <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">Sin vista previa</div>
          ) : (
            <img src={src} alt="Portada del Travel Document" className="h-full w-full object-cover" onError={() => setImgError(true)} />
          )}
        </div>
      ) : (
        <div className="flex h-28 w-48 shrink-0 items-center justify-center rounded-md border border-dashed bg-muted/40 text-xs text-muted-foreground">
          <ImageIcon className="mr-1 h-4 w-4" />Sin foto de portada
        </div>
      )}
      <div className="flex flex-col gap-1.5 pt-1">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Foto de portada</div>
        <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="td-cover-change">
          <ImageIcon className="mr-1.5 h-3.5 w-3.5" />{src ? "Cambiar portada" : "Elegir portada"}
        </Button>
        {src && (
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => onChange("")} data-testid="td-cover-remove">
            <X className="mr-1.5 h-3.5 w-3.5" />Quitar
          </Button>
        )}
      </div>
      <ImagePicker
        open={open}
        onOpenChange={setOpen}
        title="Foto de portada del Travel Document"
        orientation="horizontal"
        defaultQuery={destination || ""}
        onSelect={(picked) => {
          const url = picked?.url || picked?.preview_url || (typeof picked === "string" ? picked : "");
          if (url) { onChange(url); setOpen(false); }
        }}
      />
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// EditReservaTemplateModal (2026-08 · petición equipo)
// Modal ligero para editar los 4 campos operativos + descripción sin salir
// de la vista TD. Carga la reserva completa vía GET, guarda con PUT y
// auto-cierra + invalida queries para refrescar la vista TD.
// ─────────────────────────────────────────────────────────────────────────
function EditReservaTemplateModal({ tripId, reservaItem, onClose }) {
  const qc = useQueryClient();
  const open = !!reservaItem;
  const rid = reservaItem?.reserva_id || null;

  // Fetch de la reserva completa: necesitamos los `td_*` actuales y
  // el `confirmation_number` que no llegan en el snapshot del TD.
  const reservaQ = useQuery({
    queryKey: ["reserva-detail", tripId, rid],
    queryFn: async () => (await api.get(`/trips/${tripId}/reservas/${rid}`)).data,
    enabled: open,
  });

  // Estado local del formulario (se puebla al abrir con los datos actuales).
  const [form, setForm] = useState({
    confirmation_number: "",
    td_duration: "",
    td_meeting_point: "",
    td_contact_phone: "",
    sales_description: "",
  });

  React.useEffect(() => {
    if (reservaQ.data) {
      setForm({
        confirmation_number: reservaQ.data.confirmation_number || "",
        td_duration: reservaQ.data.td_duration || "",
        td_meeting_point: reservaQ.data.td_meeting_point || "",
        td_contact_phone: reservaQ.data.td_contact_phone || "",
        sales_description: reservaQ.data.sales_description || "",
      });
    }
  }, [reservaQ.data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      return api.put(`/trips/${tripId}/reservas/${rid}`, form);
    },
    onSuccess: () => {
      toast.success("Reserva y Travel Document actualizados");
      // Invalida el TD compose (vista actual) y la reserva.
      qc.invalidateQueries({ queryKey: ["td-compose", tripId] });
      qc.invalidateQueries({ queryKey: ["trip", tripId] });
      qc.invalidateQueries({ queryKey: ["reserva-detail", tripId, rid] });
      onClose();
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="td-edit-reserva-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-primary" />
            Editar template · {reservaItem?.service || "reserva"}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Se guarda en la reserva y se actualiza el Travel Document automáticamente.
          </p>
        </DialogHeader>
        {reservaQ.isLoading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
              <div className="sm:col-span-3">
                <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Hash className="h-3 w-3" /> Booking ID
                </Label>
                <Input
                  value={form.confirmation_number}
                  onChange={(e) => set("confirmation_number", e.target.value)}
                  placeholder="Ej. BKG-12345"
                  className="h-9"
                  data-testid="td-edit-booking-id"
                />
              </div>
              <div className="sm:col-span-2">
                <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Clock className="h-3 w-3" /> Duración
                </Label>
                <Input
                  value={form.td_duration}
                  onChange={(e) => set("td_duration", e.target.value)}
                  placeholder="Ej. 3h · half-day"
                  className="h-9"
                  data-testid="td-edit-duration"
                />
              </div>
              {!["transfer", "tren"].includes(reservaQ.data?.type) && (
              <div className="sm:col-span-4">
                <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <MapPin className="h-3 w-3" /> Meeting point
                </Label>
                <Input
                  value={form.td_meeting_point}
                  onChange={(e) => set("td_meeting_point", e.target.value)}
                  placeholder="Dirección legible"
                  className="h-9"
                  data-testid="td-edit-meeting-point"
                />
              </div>
              )}
              <div className="sm:col-span-3">
                <Label className="mb-1 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                  <Phone className="h-3 w-3" /> Contacto
                </Label>
                <Input
                  value={form.td_contact_phone}
                  onChange={(e) => set("td_contact_phone", e.target.value)}
                  placeholder="Tel. guía / conductor"
                  className="h-9"
                  data-testid="td-edit-contact"
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Descripción (aparece en el Travel Document del cliente)
              </Label>
              <RichTextEditor
                value={form.sales_description}
                onChange={(v) => set("sales_description", v)}
                placeholder="Descripción del servicio (HTML)"
                minRows={8}
                testId="td-edit-description"
              />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="td-edit-cancel">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={() => saveMut.mutate()}
            disabled={saveMut.isPending || reservaQ.isLoading}
            data-testid="td-edit-save"
          >
            {saveMut.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Check className="mr-1 h-4 w-4" />}
            Guardar y actualizar TD
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
